import { Head, Link } from "@inertiajs/react";
import { Box, Container, Flex, Grid, Heading, Section, Text } from "@radix-ui/themes";
import { Facebook, Instagram, Link as LinkIcon, MapPin } from "lucide-react";
import Breadcrumb from "@/shared/Breadcrumb";
import LikeButton from "@/shared/likes/LikeButton";
import EventFeed from "@/shared/events/EventFeed";
import type { ClubsShowProps } from "@/types/clubs_show";

export default function ClubShow({ club, upcoming, past, tracks, like }: ClubsShowProps) {
  return (
    <>
      <Head title={club.name} />
      <Section size="1" px="4">
        <Container size="4">
          {/* Header */}
          <Flex direction="column" gap="4" mb="8">
            <Box>
              <Box mb="3">
                <Breadcrumb
                  crumbs={[
                    { label: "Clubs", href: "/clubs" },
                    { label: club.name },
                  ]}
                />
              </Box>
              <Heading size="8" className="mb-3 text-xl md:text-2xl lg:text-4xl">
                {club.name}
              </Heading>
            </Box>

            {club.locale && (
              <Flex align="center" gap="1">
                <MapPin size={14} className="text-gray9" />
                <Text size="2" color="gray">
                  {club.locale}
                </Text>
              </Flex>
            )}

            <Flex gap="3">
              <LikeButton likeable={club} type="Club" like={like} />
            </Flex>
          </Flex>

          {/* Description / history + image */}
          <Grid columns={{ initial: "1", md: "2" }} gap="6" mb="8">
            <Flex direction="column" gap="4">
              {club.description && (
                <Text size="3" as="p" className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: club.description }} />
              )}
              {club.history && (
                <Text as="p" size="3" className="whitespace-pre-line" dangerouslySetInnerHTML={{ __html: club.history }} />
              )}
            </Flex>
            {club.image_url && (
              <Box>
                <img
                  src={club.image_url}
                  alt={`${club.name} logo`}
                  className="w-full rounded-lg shadow-2xl"
                />
              </Box>
            )}
          </Grid>

          {/* Social links */}
          {(club.instagram || club.facebook || club.website) && (
            <Flex direction="column" gap="2" mb="8">
              {club.instagram && (
                <a
                  href={`https://instagram.com/${club.instagram.replace(/^@+/, "")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent9 hover:underline inline-flex items-center gap-1 text-sm"
                >
                  <Instagram size={14} />
                  {club.instagram}
                </a>
              )}
              {club.facebook && (
                <a
                  href={
                    /^https?:\/\//i.test(club.facebook)
                      ? club.facebook
                      : `https://www.facebook.com/${club.facebook.replace(/^@+/, "")}`
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent9 hover:underline inline-flex items-center gap-1 text-sm"
                >
                  <Facebook size={14} />
                  {club.facebook}
                </a>
              )}
              {club.website && (
                <a
                  href={club.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accent9 hover:underline inline-flex items-center gap-1 text-sm"
                >
                  <LinkIcon size={14} />
                  {club.website}
                </a>
              )}
            </Flex>
          )}

          {/* Events: the feed the track page has. A club runs events at
              several tracks, so every event says where. */}
          <Box mt="8">
            <EventFeed
              upcoming={upcoming}
              recent={past}
              showTrack
              emptyMessage={`${club.name} has no events yet.`}
            />
          </Box>

          {/* Tracks */}
          <Box mt="8">
            <Heading size="7" mb="4">Tracks</Heading>
            {tracks.length === 0 ? (
              <Text color="gray">No tracks linked to this club yet.</Text>
            ) : (
              <Flex direction="column" gap="4">
                {tracks.map((track) => (
                  <Box
                    key={track.id}
                    className="border border-gray5 rounded-lg p-4 hover:shadow-sm transition"
                  >
                    <Flex justify="between" align="center">
                      <Link href={`/tracks/${track.slug}`}>
                        <Text size="3" weight="bold" className="hover:underline">
                          {track.name}
                        </Text>
                      </Link>
                      <Flex align="center" gap="3">
                        {(track.city || track.state) && (
                          <Flex align="center" gap="1">
                            <MapPin size={14} className="text-gray9" />
                            <Text size="2" color="gray">
                              {[track.city, track.state].filter(Boolean).join(", ")}
                            </Text>
                          </Flex>
                        )}
                        <Link href={`/tracks/${track.slug}`}>
                          <Text size="2" className="text-accent9 hover:underline">
                            View →
                          </Text>
                        </Link>
                      </Flex>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            )}
          </Box>
        </Container>
      </Section>
    </>
  );
}
